/* eslint-disable @typescript-eslint/no-explicit-any */
import { Study } from "../models/study.model";
import { RegressionStatsMapByReportContentItemId, SampleItems } from "../models/sample-analysis.model";
import { AdjustedCalFormat, AdjustedCalLots, CalAdjustmentCriteria, CalculationResponse, SampleSummaryResult} from "../models/result.model";
import { CalAssay, CalLot, CalVial, CalVialLevel } from "../models/cal-lot.model";
import { QcLot } from "../models/qc-lot.model";
import { DecimalPipe } from "@angular/common";
import { FileMetaData, ReportContentItem, ReportContentItemResponse, Sample } from "../models/run.model";
import { BulkMaterials } from "../models/assays.model";

/**
 * Function to enable handle the downloading blob response when exporting files from the server
 * @param response Blob
 * @returns 
 */
export function saveCsvZipFile(response: Blob, fileName = 'results'): boolean {
  const blob = new Blob([response], { type: 'application/gzip' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return true;
}

/**
 * Downloads a blob from the server under the given file name, preserving the
 * blob's own content type. Use this for any non-gzip download; saveCsvZipFile
 * above forces application/gzip.
 * @param response Blob returned by the API
 * @param fileName Name to save the file as
 */
export function saveBlobAsFile(response: Blob, fileName: string): boolean {
  const url = window.URL.createObjectURL(response);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return true;
}


/**
 * Function to enable Calculate button
 * @param study Study
 * @param panelName Panel Name
 * @returns 
 */
export function enableCalculate(study: Study, panelName: string): boolean {
  const hasPatientData = (study?.patientFileMetadata?.length ?? 0) > 0;
  const hasCalibrationData = (study?.calFileMetadata?.length ?? 0) > 0;
  const hasControlData = (study?.controlFileMetadata?.length ?? 0) > 0;
  const hasAnyInstrumentData = hasPatientData || hasCalibrationData || hasControlData;

  return hasAnyInstrumentData
    && study?.reportContentItems?.reportContentItems?.length > 0
    && study?.runSamples?.length > 0
    //  && study?.runSampleLots.length > 0
    && panelName?.length > 0
    //&& study?.runType > 0
}

/**
 * Function to extract Calculation Adjustment based on slope and meanBias
 * @param calcResponse Calculation Response
 * @returns 
 */
export function extractCalAdjustmentCriteria(calcResponse?: CalculationResponse | null): CalAdjustmentCriteria[] {
  if (!calcResponse?.calculationResponse?.reportContentItems?.length) {
    return [];
  }

  const adj: CalAdjustmentCriteria[] = [];

  calcResponse.calculationResponse?.reportContentItems.forEach(rp => {
    calcResponse.calculationResponse?.regressionStats
      .filter(rs => rs.reportContentItemId === (rp.kitLotNumber + rp.BeadRegion + '_' + rp.instrumentID))
      .forEach(rs => {
        rs.ranges.forEach(r => {
          if (r.rangeNo === 1 && r.biasType === 'Ratio') {
            adj.push({
              kitLotNumber: rp.kitLotNumber,
              assayName: rp.assayName,
              region: rp.BeadRegion,
              slope: r.slope,
              meanBias: r.meanBias,
              adjustedConcs: []
            });
          }
        });
      });
  });

  return adj;
}

/**
 * Applies calibration adjustments (mean bias and slope) to the original calibration lots
 * based on the calculation response and returns a new adjusted calibration lot structure.
 *
 * Each vial value is recalculated using the adjusted slope and mean bias, and formatted
 * according to the specified decimal precision.
 *
 * @param calcResponse The calculation response containing calibration adjustment criteria.
 * @param adjustedCalLots The original calibration lot data including value assignments.
 * @returns An object containing:
 * - `originalCalLot`: the unmodified calibration lots,
 * - `adjustedCalLot`: the recalculated lots based on slope and bias,
 * - `valueAssignment`: the original value assignments.
 */
export function createAdjustedCalLots(calcResponse: CalculationResponse, adjustedCalLots: AdjustedCalFormat) {
  const calAdjustmentCriteria = extractCalAdjustmentCriteria(calcResponse);
  const originalValForChange = adjustedCalLots.originalCalLot;
  const decimalPipe = new DecimalPipe('en-US');

  const valUpdate: typeof adjustedCalLots['adjustedCalLot'] = [];
  for (const calLot of originalValForChange) {
    const updatedCalLot = { ...calLot };
    const adjustments = calAdjustmentCriteria.filter((adj) => adj.region === updatedCalLot.region);
    if (adjustments.length === 0) {
      const updatedVials = { ...updatedCalLot.vials };
      for (const vialNumber of Object.keys(updatedVials)) {
        const decimalPrecision = updatedCalLot.decimalPrecision;
        const transformedVialValue = decimalPipe.transform(
          // TODO: Determine to bring back this formula or not
          // getAdjustedValue(parseFloat(updatedVials[vialNumber]), updatedCalLot.meanBias, updatedCalLot.slope),
          (updatedVials[vialNumber]),
          '1.' + decimalPrecision + '-' + decimalPrecision
        ) + '';
        updatedVials[vialNumber] = transformedVialValue;
      }
      updatedCalLot.vials = updatedVials;
      valUpdate.push(updatedCalLot);
      continue;
    }

    for (const adjustment of adjustments) {
      const updatedCalLotCalLotForCurrentAdj = { ...updatedCalLot };

      updatedCalLotCalLotForCurrentAdj.kitLotNumber = adjustment.kitLotNumber;
      updatedCalLotCalLotForCurrentAdj.meanBias = adjustment.meanBias;
      updatedCalLotCalLotForCurrentAdj.slope = adjustment.slope;

      const updatedVials = { ...updatedCalLotCalLotForCurrentAdj.vials };
      for (const vialNumber of Object.keys(updatedVials)) {
        const decimalPrecision = updatedCalLotCalLotForCurrentAdj.decimalPrecision;
        const transformedVialValue = decimalPipe.transform(
          // TODO: Determine to bring back this formula or not
          // getAdjustedValue(parseFloat(updatedVials[vialNumber]), updatedCalLotCalLotForCurrentAdj.meanBias, updatedCalLotCalLotForCurrentAdj.slope),
          (updatedVials[vialNumber]),
          '1.' + decimalPrecision + '-' + decimalPrecision
        ) + '';
        updatedVials[vialNumber] = transformedVialValue;
      }
      updatedCalLotCalLotForCurrentAdj.vials = updatedVials;
      valUpdate.push(updatedCalLotCalLotForCurrentAdj);
    }
  }

  const returnVal = {
    originalCalLot: originalValForChange,
    adjustedCalLot: valUpdate,
    valueAssignment: adjustedCalLots.valueAssignment
  };

  return returnVal;
}

/**
 * getAdjustedValue function is used to calculate the value based on meanbias, slope and concentration
 * if the adjusted concentration is null or less than 0, it returns the original concentration.
 * @param concentration number
 * @param bias number
 * @param slope number
 * @returns calculated value
 */
export function getAdjustedValue(concentration: number, bias: number, slope: number): number {
  const biasValue = Number.isFinite(bias) ? bias : 0;
  const slopeValue = Number.isFinite(slope) && slope !== 0 ? slope : 1;
  const adjConc = (concentration - biasValue) / slopeValue;
  if (!Number.isFinite(adjConc) || adjConc < 0) {
    return concentration;
  }
  else {
    return adjConc
  }
}

///**
// * getSampleStatusText function returns the status name corresponding to a sample's result status.
// * @param sampleStatus The result status enum value.
// * @returns The human-readable result status name, or an empty string if not found.
// */
//export function getSampleStatusText(sampleStatus: ResultStatusEnum | undefined): string {
//  return resultStatusTypes.find(type => type.resultStatusId === sampleStatus)?.resultStatusName || '';
//}

/**
 * mapCommonSampleItems function Maps a sample summary result and its regression stats to a structured object for display or reporting.
 * @param sample The sample summary data.
 * @param stats Regression statistics mapped by report content item ID.
 * @returns A `SampleItems` object with mapped and formatted sample values.
 */
export function mapCommonSampleItems(sample: SampleSummaryResult, stats: RegressionStatsMapByReportContentItemId | undefined): SampleItems {
  const commonSampleItems: SampleItems = {
    sampleId: sample.accessionNo,
    resultStatus: sample.resultStatus,
    assignment: sample.assignedValue ?? NaN,
    alertLow: sample.alertLow ?? '',
    alertHigh: sample.alertHigh ?? '',
    failLow: sample.failLow ?? '',
    failHigh: sample.failHigh ?? '',
    acceptanceRangeStatus: sample.acceptanceRangeStatus ?? '',
    rangeOnly: sample.rangeOnly ?? '',
    inRange1: stats?.ranges?.[0]?.rangeNo ? 'x' : '',
    inRange2: stats?.ranges?.[1]?.rangeNo ? 'x' : '',
    inRange3: stats?.ranges?.[2]?.rangeNo ? 'x' : '',
    inRange4: stats?.ranges?.[3]?.rangeNo ? 'x' : '',
    formattedTestResult: sample.formattedTestResult ?? NaN,
    testScore: sample.testScore ?? '',
    difference: sample.difference ?? NaN,
    percentDifference: sample.percentDifference ?? NaN,
    ratio: sample.ratio ?? NaN,
    repSD: sample.repSD ?? NaN,
    repCV: sample.repCV ?? NaN,
    resultRepOK: sample.resultRepOk ?? '',
    averageBeadCV: sample.meanBeadCV ?? NaN,
    averageBeadCount: sample.meanBeadCount ?? NaN,
    averageCountTime: sample.meanCountTime ?? NaN,
    finalRepCount: sample.finalRepCount ?? NaN,
    repCountOk: sample.repCountOk ?? ''
  }

  return commonSampleItems;
}

/** Returns a new array of unique cal lots by calLotNumber + valueAssignmentVersion (keeps first occurrence). */
export function removeDuplicateCalLots(calLots: CalLot[]): CalLot[] {
  const seen = new Set<string>();
  const uniqueCallots: CalLot[] = [];
  for (const lot of calLots) {
    const lotNumber = lot?.calLotNumber ?? '';
    const key = `${lotNumber}::${lot?.valueAssignmentVersion ?? ''}`;
    if (!lotNumber || !seen.has(key)) {
      if (lotNumber) seen.add(key);
      uniqueCallots.push(lot);
    }
  }
  return uniqueCallots;
}

/** True when a "Cal - Cal" run sample's accession or sample ID matches one of the lot's vial barcodes. */
function hasCalCalSampleForLot(calLot: CalLot, runSamples: Sample[]): boolean {
  const normalize = (value: string | null | undefined) => (value ?? '').trim().toUpperCase();
  const barcodes = new Set((calLot?.vials ?? []).map(vial => normalize(vial.barcode)).filter(Boolean));
  if (!barcodes.size) {
    return false;
  }
  return runSamples.some(sample =>
    normalize(sample.sampleType) === 'CAL - CAL' &&
    (barcodes.has(normalize(sample.accessionId)) || barcodes.has(normalize(sample.sampleId)))
  );
}

/**
 * Resolves the cal lot for one report content item from the "Cal - Cal" sample types it actually
 * uses. Each such sample's ID (e.g. "CALIBF110DL01") is parsed for the lot number of an imported
 * lot ("F110D"); when several lots are found, the one covering `region` wins. Returns undefined when
 * no Cal - Cal sample names an imported lot, so the caller can fall back to resolveCalLotForRegion.
 */
export function resolveCalLotFromSampleTypes(
  allCalLots: CalLot[] | undefined,
  region: number | undefined,
  itemSamples: { sampleId?: string | null; sampleType?: string | null }[] | undefined
): CalLot | undefined {
  const normalize = (value: string | null | undefined) => (value ?? '').replace(/\s+/g, '').toUpperCase();
  if (!allCalLots?.length || !itemSamples?.length) {
    return undefined;
  }
  const usedLots: CalLot[] = [];
  itemSamples
    .filter(sample => normalize(sample.sampleType) === 'CAL-CAL')
    .forEach(sample => {
      const sampleId = normalize(sample.sampleId);
      // Longest lot number first, so one lot number contained in another cannot shadow it.
      const lot = [...allCalLots]
        .sort((a, b) => (b.calLotNumber ?? '').length - (a.calLotNumber ?? '').length)
        .find(calLot => !!calLot.calLotNumber && sampleId.includes(normalize(calLot.calLotNumber)));
      if (lot && !usedLots.some(used => used.calLotNumber === lot.calLotNumber)) {
        usedLots.push(lot);
      }
    });
  if (!usedLots.length) {
    return undefined;
  }
  return usedLots.find(calLot => (calLot.assays ?? []).some(assay => assay.region === region)) ?? usedLots[0];
}

/**
 * Resolves which calibration lot applies to a report content item's region. `reagentLot` is not
 * used for this — a lot flagged `assayPanelName === "All"` is checked first, since more than one
 * "All" lot can exist (e.g. a later import overriding a handful of regions while the original lot
 * still covers the rest) and only which one's own `assays` array actually lists the requested
 * region tells them apart. When no "All" lot covers the region, any other (non-"All") lot whose
 * `assays` array lists it is used instead — that lot's own `assayPanelName` does not matter here.
 *
 * When `runSamples` is supplied, only lots confirmed by the run are considered: the run must contain
 * a sample of type "Cal - Cal" whose accession or sample ID matches one of the lot's vial barcodes.
 * If no lot is confirmed, the non-"All" lots are used instead (region match first, otherwise the
 * first one). Omit `runSamples` to skip the check entirely.
 */
export function resolveCalLotForRegion(
  allCalLots: CalLot[] | undefined,
  region: number | undefined,
  runSamples?: Sample[]
): CalLot | undefined {
  const isAllLot = (calLot: CalLot) => (calLot.assayPanelName ?? '').toUpperCase() === 'ALL';
  let calLots = allCalLots;
  if (runSamples) {
    const confirmedLots = allCalLots?.filter(calLot => hasCalCalSampleForLot(calLot, runSamples));
    // No lot confirmed by the run's Cal - Cal samples: default to the non-"All" lots.
    calLots = confirmedLots?.length ? confirmedLots : allCalLots?.filter(calLot => !isAllLot(calLot));
  }
  if (!calLots?.length) {
    return undefined;
  }

  const allLots = calLots.filter(calLot => (calLot.assayPanelName ?? '').toUpperCase() === 'ALL');
  const allLotsMatch = allLots.find(calLot => (calLot.assays ?? []).some(assay => assay.region === region));
  if (allLotsMatch) {
    return allLotsMatch;
  }

  const otherLots = calLots.filter(calLot => !allLots.includes(calLot));
  const otherLotMatch = otherLots.find(calLot => (calLot.assays ?? []).some(assay => assay.region === region));

  return otherLotMatch ?? calLots[0] ?? allLots[0];
}

/**
 * Function to extracts and formats concentration values for each region from the given calibration lots.
 * @param calLots Array of calibration lots to process.
 * @returns An array of adjusted calibration lot data grouped by region with formatted vial concentrations.
 */
export function getConcentrationsByRegion(calLot: CalLot) {
  const result: AdjustedCalLots[] = [];

    calLot.assays.forEach((assay: CalAssay) => {
      const region = assay.region;
      let regionObj = result.find((item: AdjustedCalLots) => item.region === region);

      if (!regionObj) {
        regionObj = { kitLotNumber: '', assayName: assay.assayName, region: region, vials: {}, decimalPrecision: assay.decimalPrecision, meanBias: 0, slope: 1 };
        result.push(regionObj);
      }

      calLot.vials.forEach((vial: CalVial) => {
        const vialLevel = vial.levels.find((level: CalVialLevel) => level.region === region);
        if (vialLevel && regionObj) {
          const vialLevelConcentration = (vialLevel.concentration).toString();
          regionObj.vials[vial.barcode] = parseFloat(vialLevelConcentration).toFixed(2)
        }
      });
    });

  return result;
}
/**
 * Returns the concentration for a given assay region from the vial's levels.
 * 
 * @param vial - The vial containing multiple level data.
 * @param assay - The assay for which region concentration is needed.
 * @returns The concentration value for the matched region or an empty string.
 */
export function getLevelConcentrationForRegion(vial: CalVial, assay: CalAssay): number | string {
  let vialLevel: CalVialLevel | undefined;

  if (assay && vial.levels && vial.levels.length) {
    vialLevel = vial.levels.find((level: CalVialLevel) => level.region === assay.region);
  }

  return vialLevel ? vialLevel.concentration : '';
}
/**
 * Rounds a value to the given decimal precision using the same arithmetic as the Adjusted Calibrator
 * page's `getFormattedMeanValue`, so values persisted to the calculation request match what the user
 * sees in the top section of that page.
 * @param value The raw value to round.
 * @param precision The number of decimal places to round to (negative values are treated as 0).
 */
export function roundToDisplayPrecision(value: number, precision: number): number {
  const clampedPrecision = precision >= 0 ? precision : 0;
  const factor = Math.pow(10, clampedPrecision);
  return clampedPrecision > 0 ? Math.round(value * factor) / factor : Math.round(value);
}

/**
 * Renders a value the UI derived itself, dropping the binary floating-point tail that arithmetic
 * on decimal inputs leaves behind (`0.1 * 3` is 0.30000000000000004). Twelve significant digits
 * is far past any spec's real precision and far short of where the noise starts, so the value
 * keeps its own decimals instead of being forced to a display precision it may not fit.
 * @param value The derived value to render.
 */
export function formatComputedNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return String(Number(value.toPrecision(12)));
}

/**
 * Finds the adjusted cal lot entry a report content item is displayed as, most specific key first:
 * the item's kit lot and region, then an entry that has no kit lot of its own.
 *
 * A bead region within a kit lot is the identity that matters, so the assay name only breaks ties.
 * Requiring the assay name to match dropped the operator's values from the request, because the run
 * report items carry the user's assay names while the entries carry the calibrator lot's. An entry
 * with no kit lot is one the calculation response reported no adjustment criteria for, so it belongs
 * to whichever item shares its region. An entry naming a *different* kit lot is never used.
 */
function findAdjustedLotForReportItem(adjustedCalLot: AdjustedCalLots[], item: ReportContentItem): AdjustedCalLots | undefined {
  const sameRegion = adjustedCalLot.filter(lot => Number(lot.region) === Number(item.region));
  if (!sameRegion.length) {
    return undefined;
  }

  const sameKitLot = sameRegion.filter(lot => lot.kitLotNumber === item.lot);
  const unassigned = sameRegion.filter(lot => !lot.kitLotNumber);
  return sameKitLot.find(lot => lot.assayName === item.assay)
    ?? sameKitLot[0]
    ?? unassigned.find(lot => lot.assayName === item.assay)
    ?? unassigned[0];
}

/**
 * Constructs value assignment data from the adjusted calibration lots, one row per report content item.
 *
 * A row is sent whenever the adjusted cal lots hold the entry that item is displayed as — which is
 * exactly what the Adjusted Calibrator page shows and what the operator edits — so what is on screen
 * is what goes out. Re-checking the adjustment criteria here added nothing except a failure mode:
 * they are recomputed from whichever calculation response came back last, and a response that no
 * longer reports them blanked every row.
 *
 * @param reportContentItemsData Report content items to align with calibration lots.
 * @param adjustedCalLotData Object containing original and adjusted calibration lot data.
 * @returns An object containing the original and adjusted cal lots, and the calculated value assignments.
 */
export function getValueAssignmentData(reportContentItemsData: ReportContentItem[], adjustedCalLotData: AdjustedCalFormat) {
  // Flattens a cal lot's vial concentrations into a sorted value-assignment row of only the real,
  // vial-derived cal levels. When `round` is set, each value is rounded to the lot's (region's) decimal precision so
  // it matches the Adjusted Calibrator page display.
  const buildRow = (lot: AdjustedCalLots | undefined, _assay: string, round = false): number[] => {
    if (!lot) {
      return [];
    }
    const vialsData = Object.values(lot.vials).map(value => {
      const num = parseFloat(value);
      return round && Number.isFinite(num) ? roundToDisplayPrecision(num, lot.decimalPrecision ?? 0) : num;
    });
    const row = vialsData.filter((n): n is number => Number.isFinite(n));
    row.sort((a, b) => a - b);
    return row;
  };

  const result: number[][] = [];
  const originalResult: number[][] = [];
  reportContentItemsData.forEach(criteria => {
    const matchedRegion = findAdjustedLotForReportItem(adjustedCalLotData.adjustedCalLot, criteria);

    if (matchedRegion) {
      result.push(buildRow(matchedRegion, criteria.assay));
      // Original lots carry an empty kitLotNumber (see getConcentrationsByRegion), so the same
      // region-first matching resolves them.
      const matchedOriginal = findAdjustedLotForReportItem(adjustedCalLotData.originalCalLot ?? [], criteria);
      originalResult.push(buildRow(matchedOriginal, criteria.assay, true));
    } else {
      result.push([]);
      originalResult.push([]);
    }
  });

  // Prefer an existing (page-computed, top-section) original value assignment; otherwise fall back to
  // the values derived from the original cal lot so the auto-generated adjustment path still sends them.
  const originalValueAssignment = adjustedCalLotData.originalValueAssignment?.length
    ? adjustedCalLotData.originalValueAssignment
    : originalResult;

  return { "originalCalLot": adjustedCalLotData.originalCalLot, "adjustedCalLot": adjustedCalLotData.adjustedCalLot, "valueAssignment": result, "originalValueAssignment": originalValueAssignment };
}

/**
 * Converts a space-separated string to camelCase and appends 'Ln' at the end.
 * @param bulk_material_description The input string to transform.
 * @returns A camelCase version of the string with 'Ln' appended.
 */
export function transformToCamelCase(bulk_material_description: string): string {
  return bulk_material_description
    .split(' ')
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('') + 'Ln';
}

/**
 * Compares two arrays of adjusted calibration lots to check if they are identical
 * in region, mean bias, slope, decimal precision, and vial values.
 * @param original The original calibration lot array.
 * @param adjusted The adjusted calibration lot array.
 * @returns True if arrays are identical in structure and values, false otherwise.
 */
export function compareArrays(original: AdjustedCalLots[], adjusted: AdjustedCalLots[]): boolean {
  if (!original?.length && !adjusted?.length) {
    return true;
  }
  if (!original?.length || !adjusted?.length || original.length !== adjusted.length) {
    return false;
  }
  for (let i = 0; i < original.length; i++) {
    const origItem = original[i];
    const adjItem = adjusted[i];

    if (!origItem || !adjItem) {
      return false;
    }
    if (origItem.region !== adjItem.region) {
      return false;
    }
    if (origItem.meanBias !== adjItem.meanBias) {
      return false;
    }
    if (origItem.slope !== adjItem.slope) {
      return false;
    }
    if (origItem.decimalPrecision !== adjItem.decimalPrecision) {
      return false;
    }

    const originalVials = origItem.vials ?? {};
    const adjustedVials = adjItem.vials ?? {};
    for (const vialKey in originalVials) {
      const originalValue = parseFloat(originalVials[vialKey]).toFixed(2);
      const adjustedValue = parseFloat(adjustedVials[vialKey] || "").toFixed(2);

      if (originalValue !== adjustedValue) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Determines the overall calculation status from the backend-computed studyStatus.
 * The adjusted response's status takes precedence over the original's, since it supersedes it.
 * @returns 'FAIL', 'ALERT', 'PASS', or 'NA'.
 */
export function checkCalculateStatus(
  originalResponse?: CalculationResponse | null,
  adjustedResponse?: CalculationResponse | null
): string {
  return adjustedResponse?.calculationResponse?.studyStatus?.overallStatus
    ?? originalResponse?.calculationResponse?.studyStatus?.overallStatus
    ?? 'NA';
}

/**
 * Extracts a unique list of reagent lot numbers from the given file metadata.
 * @param data Array of file metadata containing reagent lot information.
 * @returns An array of unique reagent lot strings.
 */
export function getUniqueReagentLots(data: FileMetaData[]): string[] {
  const lotSet = new Set<string>();

  data.forEach(entry => {
    (entry.reagentLots || []).forEach(lot => {
      lotSet.add(String(lot));
    });
  });

  return Array.from(lotSet);
}

/**
 * Extracts a unique list of accession numbers from the given file metadata.
 * @param data Array of file metadata containing accession information.
 * @returns An array of unique accession strings.
 */
export function getUniqueAccessions(data: FileMetaData[]): string[] {
  const accessionSet = new Set<string>();
  data.forEach(entry => {
    (entry.accessions || []).forEach((acc: string) => {
      accessionSet.add(acc.trim().toUpperCase());
    });
  });
  return Array.from(accessionSet);
}

///**
// * Gets the textual description of a run type based on its enum ID.
// * @param runType The run type identifier.
// * @returns The name of the run type, or an empty string if not found.
// */
//export function getRunTypeText(runType: PhaseTypeEnum | undefined): string {
//  return PhaseTypes.find(type => type.PhaseTypeId === runType)?.PhaseTypeName ?? '';
//}

///**
// * Gets the textual description of a phase type by its enum ID.
// * @param phaseTypeId The phase type identifier.
// * @returns The name of the phase type, or an empty string if undefined.
// */
//export function getPhaseTypeText(phaseTypeId: PhaseTypeEnum | undefined): string {
//  return phaseTypeId ? getPhaseTypeById(phaseTypeId) : '';
//}

///**
// * Retrieves the phase name based on the run type ID.
// * @param runType The run type ID to look up.
// * @returns The corresponding phase name, or an empty string if not found.
// */
//export function getPhaseName(runType: number | undefined): string {
//  const phase = PhaseTypes.find(p => p.PhaseTypeId === runType);
//  return phase ? phase.PhaseTypeName : '';
//}


/**
 * Extracts and transforms bulk material descriptions into camelCase column headers.
 * @param bulkMaterials - List of materials used to generate dynamic column names.
 */
export function reportContentItemResponseFunc(bulkMaterials: BulkMaterials[]): ReportContentItemResponse {
  const variableColumns:string[] = [];

  bulkMaterials.forEach((element: BulkMaterials) => {
    variableColumns.push(transformToCamelCase(element.bulk_material_description));
  });

  return {
    variableColumns: variableColumns,
    reportContentItems: []
  } as ReportContentItemResponse
}


export function displayAsPercent(value: any, factor: number): string {
  if (value === null || value === undefined || Number.isNaN(value) || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return (num * factor).toFixed(1) + ' %';
}

/**
 * Determines whether a lot expiration date (e.g. "11/30/2029") has already passed as of today.
 * @param expirationDate The lot's expiration date string.
 * @returns True if the date parses successfully and is strictly before today.
 */
export function isDateExpired(expirationDate: string): boolean {
  const parsedDate = new Date(expirationDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsedDate.getTime() < today.getTime();
}

/**
 * Normalizes a QC lot response from the Control CofA endpoint. That endpoint returns
 * the expiration date under the PascalCase key "ExpirationDate" instead of the
 * camelCase "controlLotExpirationDate" used everywhere else in the app, so without
 * this the field is silently undefined on every imported control lot.
 * @param qcLot Raw QC lot response from the API.
 * @returns The QC lot with `controlLotExpirationDate` populated.
 */
export function normalizeQcLot(qcLot: QcLot): QcLot {
  const rawQcLot = qcLot as QcLot & { ExpirationDate?: string };
  if (!qcLot.controlLotExpirationDate && rawQcLot.ExpirationDate) {
    qcLot.controlLotExpirationDate = rawQcLot.ExpirationDate;
  }
  return qcLot;
}

/**
 * Formats a raw cell value for display and for column-filter comparison in a
 * Material table, matching the formatting SharedTableComponent's column filters use
 * (e.g. on the Calculations table's Accession column) so filter values line up with
 * what's shown on screen.
 * @param value The raw cell value.
 */
export function formatTableCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : '-';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '-';
  }
  return String(value);
}

export function displayOrDash(value: any): string {
  if (value === 0 || value === false) return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '-';
    }
    const normalized = trimmed.toLowerCase();
    if (normalized === 'none' || normalized.replace(/\s+/g, '') === 'nonetonone') {
      return '-';
    }
    return trimmed;
  }
  if (Array.isArray(value)) return value.length === 0 ? '-' : String(value);
  if (value && typeof value === 'object') {
    if (value instanceof Date) return value.toString();
    return Object.keys(value).length === 0 ? '-' : String(value);
  }
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return String(value);
}

/**
 * Renders a min/max spec pair as a single value. A one-sided spec reads as an inequality so the
 * missing bound is not mistaken for a real limit.
 * @param min The lower spec bound, if any.
 * @param max The upper spec bound, if any.
 */
export function formatSpecRange(min: unknown, max: unknown): string {
  const lower = displayOrDash(min);
  const upper = displayOrDash(max);
  if (lower !== '-' && upper !== '-') {
    return `${lower} to ${upper}`;
  }
  if (lower !== '-') {
    return `≥ ${lower}`;
  }
  if (upper !== '-') {
    return `≤ ${upper}`;
  }
  return '-';
}

/**
 * Header suffix for the column holding the adjusted half of a calculation. In Final mode that
 * column holds the Calibrator Adjustment's Final run instead.
 */
export function adjustedColumnLabel(isFinal: boolean): string {
  return isFinal ? '(final)' : '(adjusted)';
}

/** Tint class for the column holding the adjusted half, or the Final run in Final mode. */
export function adjustedColumnClass(isFinal: boolean): string {
  return isFinal ? 'final-column-highlight' : 'adjusted-column-highlight';
}

/**
 * Final mode shows one set of result columns, the Final run's. Labels every column that has an
 * adjusted counterpart (final), and returns those keys so the table can tint them.
 */
export function buildFinalColumns(
  keyColumns: Record<string, string>,
  keyColumnsWithAdj: Record<string, string>
): { columns: Record<string, string>; finalKeys: string[] } {
  const finalKeys = Object.keys(keyColumns).filter(key => `${key}Adj` in keyColumnsWithAdj);
  const columns = Object.fromEntries(Object.entries(keyColumns).map(([key, label]) =>
    [key, finalKeys.includes(key) ? `${label} ${adjustedColumnLabel(true)}` : label]
  ));
  return { columns, finalKeys };
}

/**
 * The samples a Final run's screens show. They are expected under the adjusted half, but fall back to
 * the unadjusted one in case the backend files a single run's samples there.
 */
export function finalSampleData<T>(adjusted: T[] | undefined, original: T[] | undefined): T[] {
  return adjusted?.length ? adjusted : (original ?? []);
}
